import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import HeroCarousel from "@/components/feature/HeroCarousel";
import MovieCard from "@/components/feature/MovieCard";
import NewsListCard from "@/components/feature/NewsListCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/lib/seo";
import {
  getHotMovies,
  getLatestNews,
  getMovieById,
  mediaTypeLabel,
  movies,
  topics,
  type Movie,
} from "@/lib/media";

const featuredIds = ["movie-01", "movie-02", "tv-01", "variety-01"];

const quickLinks = [
  {
    type: "movie" as const,
    label: "电影",
    desc: "院线新片与经典佳作，按类型与地区快速筛选",
    icon: "ri-film-line",
    tone: "bg-primary-50 text-primary-700",
  },
  {
    type: "tv" as const,
    label: "电视剧",
    desc: "正在更新的华语剧与海外剧集，追剧不迷路",
    icon: "ri-tv-2-line",
    tone: "bg-secondary-50 text-secondary-700",
  },
  {
    type: "variety" as const,
    label: "综艺",
    desc: "慢综艺、竞演与真人秀，轻松一点的选择",
    icon: "ri-mic-line",
    tone: "bg-accent-50 text-accent-800",
  },
];

export default function Home() {
  usePageMeta({
    title: "枫与铃 - 最新影视剧推荐与精彩影视资讯平台",
    description:
      "枫与铃网站致力于为用户提供丰富的影视剧资源和最新影视资讯，覆盖电影、电视剧、综艺等多种类型，满足观影和资讯查询需求。",
    keywords: "枫与铃,影视剧,电影推荐,电视剧追剧,影视资讯,热门电影,综艺节目",
  });

  const [heroItems] = useState<Movie[]>(
    featuredIds.map((id) => getMovieById(id)).filter((item): item is Movie => Boolean(item))
  );

  const latestMovies = [...movies]
    .sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1))
    .slice(0, 4);
  const latestNews = getLatestNews(5);
  const hotMovies = getHotMovies(5);
  const featuredTopic = topics[0];
  const sideTopics = topics.slice(1);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <HeroCarousel items={heroItems} />

      <main>
        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid gap-4 md:grid-cols-3 md:gap-5">
            {quickLinks.map((link) => {
              const count = movies.filter((item) => item.type === link.type).length;
              return (
                <Link
                  key={link.type}
                  to={`/category/${link.type}`}
                  className="group flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-all hover:border-primary-300 hover:bg-primary-50/40"
                >
                  <div className="flex items-start gap-4">
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${link.tone}`}
                    >
                      <i className={`${link.icon} text-xl`} />
                    </span>
                    <div>
                      <h2 className="font-heading text-base font-semibold text-foreground-950">
                        {link.label}
                      </h2>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {link.desc}
                      </p>
                      <span className="mt-3 inline-block text-[11px] text-foreground-500">
                        共 {count} 部作品
                      </span>
                    </div>
                  </div>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-500 transition-colors group-hover:border-primary-400 group-hover:text-primary-600">
                    <i className="ri-arrow-right-up-line text-sm" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pb-10 md:px-8 md:pb-14">
          <SectionHeading
            eyebrow="Now Showing"
            title="最新上映影视推荐"
            description="按上线时间整理的新片与新剧，包含剧情、悬疑、科幻、治愈等多种口味。"
            actionLabel="查看全部电影"
            actionTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100/70">
          <div className="mx-auto grid w-full max-w-[1280px] gap-8 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[1.8fr_1fr]">
            <div>
              <SectionHeading
                eyebrow="News"
                title="最新影视资讯"
                description="预告、评论、采访与产业动态，每天更新几条值得读的。"
                actionLabel="更多资讯"
                actionTo="/news"
              />
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-2">
                {latestNews.map((item) => (
                  <NewsListCard key={item.id} item={item} />
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">
                    热度榜
                  </h2>
                  <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                    <i className="ri-fire-line text-base" />
                  </span>
                </div>
                <ol className="flex flex-col">
                  {hotMovies.map((movie, index) => (
                    <li key={movie.id}>
                      <Link
                        to={`/detail/${movie.id}`}
                        className="group flex cursor-pointer items-center gap-3 border-b border-background-200 py-2.5 last:border-b-0"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-semibold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                            {movie.title}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-foreground-500">
                            {mediaTypeLabel[movie.type]} · {movie.region}
                          </span>
                        </span>
                        <span className="font-heading text-xs font-semibold text-primary-600">
                          {movie.score.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  本周编辑手记
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  这一周我们重看了《归途有风》。它提醒我们，有些关系不需要被解决，只需要被多陪一段路。
                </p>
                <Link
                  to="/news/news-07"
                  className="mt-4 flex cursor-pointer items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  阅读这篇评论
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-s-line text-base" />
                  </span>
                </Link>
              </div>
            </aside>
          </div>
        </section>

        <section id="topics" className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <SectionHeading
            eyebrow="Collections"
            title="视频栏目专题"
            description="把相似的作品与相关资讯整理在一起，方便一次看完一个主题。"
            actionLabel="浏览全部专题"
            actionTo={`/topic/${featuredTopic.id}`}
          />

          <div className="grid gap-4 lg:grid-cols-[1.7fr_1fr] lg:gap-5">
            <Link
              to={`/topic/${featuredTopic.id}`}
              className="group relative block h-[300px] cursor-pointer overflow-hidden rounded-lg bg-foreground-950 md:h-[380px]"
            >
              <img
                src={featuredTopic.cover}
                alt={featuredTopic.title}
                title={`${featuredTopic.title} 影视专题`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <span className="rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-foreground-950">
                  精选专题
                </span>
                <h3 className="mt-3 font-heading text-xl font-bold text-background-50 md:text-3xl">
                  {featuredTopic.title}
                </h3>
                <p className="mt-2 max-w-lg text-sm text-background-50/85">{featuredTopic.subtitle}</p>
                <span className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50">
                  进入专题
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </span>
              </div>
            </Link>

            <div className="grid gap-4">
              {sideTopics.map((topic) => {
                const topicNewsCount = topic.newsIds.length;
                return (
                  <Link
                    key={topic.id}
                    to={`/topic/${topic.id}`}
                    className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <div className="h-[104px] w-[104px] shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={topic.cover}
                        alt={topic.title}
                        title={`${topic.title} 影视专题`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-col justify-center">
                      <h3 className="font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                        {topic.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {topic.subtitle}
                      </p>
                      <span className="mt-3 text-[11px] text-foreground-500">
                        {topic.movieIds.length} 部影视 · {topicNewsCount} 篇资讯
                      </span>
                    </div>
                  </Link>
                );
              })}

              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
                <div className="flex items-center gap-2 text-primary-600">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <span className="text-xs font-medium">想看什么，告诉我们</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  专题每两周更新一次，依据站内热度与编辑推荐整理，也欢迎通过联系我们提交片单建议。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100/70">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
            <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:p-8">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  想找一部特定的作品？
                </h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
                  全站搜索同时覆盖影视作品与资讯文章，也可以从分类页按类型、地区、年份逐层筛选。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/search"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-search-line text-sm" />
                  </span>
                  开始搜索
                </Link>
                <Link
                  to="/favorites"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  查看我的收藏
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}